import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { STDoc } from './stationery-doc';
import { Ic } from './icons';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — paginator + page frame
   • Measures every flow block once (off-screen) and flows it onto
     real pages. Content height is a DETERMINISTIC function of the
     page size, margins and header/footer heights — so the same
     inputs always yield the same page count (no density flip-flop).
   • The [data-split] line-item table splits row-by-row with its
     <thead> repeated on continuation pages.
   • Running header & footer are fully configurable (height, which
     fields, logo, page numbers, which pages they repeat on).
   • [data-edit] fields become click-to-edit in the preview.
   Exposes export const PBPaper = { PaperDoc, dims }
   ───────────────────────────────────────────────────────────── */

  const { useRef, useState, useLayoutEffect, useEffect } = React;

  const SIZES = { A4: [794, 1123], Letter: [816, 1056] };
  const PAD_X = 52;          // left/right content margin
  const BASE_MARGIN = 42;    // minimum top/bottom content margin
  const BAND_GAP = 10;       // gap between a running band and the content
  const EPS = 1.6;           // tolerance so near-fit blocks don't flip a page

  function dims(paper, orient) {
    let [w, h] = SIZES[paper] || SIZES.A4;
    if (orient === 'landscape') { const t = w; w = h; h = t; }
    return { w, h, cw: w - PAD_X * 2 };
  }

  // margins reserved for the running header/footer bands
  function margins(frame) {
    const f = frame || {};
    const h = f.header || {}, ft = f.footer || {};
    const top = h.show ? Math.max(BASE_MARGIN, (h.height || 34) + BAND_GAP) : BASE_MARGIN;
    const bot = ft.show ? Math.max(BASE_MARGIN, (ft.height || 34) + BAND_GAP) : BASE_MARGIN;
    return { top, bot };
  }

  function bandShown(cfg, pageIndex) {
    if (!cfg || !cfg.show) return false;
    if (cfg.repeat === 'first') return pageIndex === 0;
    if (cfg.repeat === 'subsequent') return pageIndex > 0;
    return true; // 'all'
  }

  const px = (v) => parseFloat(v) || 0;
  function attrsOf(el) {
    let s = '';
    for (const a of el.attributes) s += ` ${a.name}="${a.value.replace(/"/g, '&quot;')}"`;
    return s;
  }

  // ── core layout ──────────────────────────────────────────────
  // core layout. `chFn(pageIndex)` returns the usable content height for that
  // page — it varies because the stationery header is full on page 1 but slim
  // (or absent) on continuation pages, and footers may be first-page-only.
  function paginate(measureRoot, chFn) {
    // ignore display:none blocks (e.g. the body's .pbd-foot when a stationery
    // owns the footer) — a hidden node returns a zero rect that would otherwise
    // be mis-measured as a full-page-tall block and spawn an empty trailing page.
    const kids = [...measureRoot.children].filter(k => getComputedStyle(k).display !== 'none');
    const rootTop = measureRoot.getBoundingClientRect().top;
    const rects = kids.map(k => k.getBoundingClientRect());
    const blocks = kids.map((el, i) => {
      const top = rects[i].top - rootTop;
      const bottom = (i < kids.length - 1 ? rects[i + 1].top : measureRoot.scrollHeight + rootTop) - rootTop;
      return { el, h: Math.max(rects[i].height, bottom - top), split: el.hasAttribute('data-split') };
    });

    const pages = [[]];
    let cur = 0;
    let CH = chFn(0);
    const newPage = () => { pages.push([]); cur = 0; CH = chFn(pages.length - 1); };
    const place = (item, h) => { pages[pages.length - 1].push(item); cur += h; };

    for (const b of blocks) {
      if (!b.split) {
        if (cur > 0 && cur + b.h > CH + EPS) newPage();
        place({ kind: 'block', html: b.el.outerHTML }, b.h);
        continue;
      }
      // splittable line-item table
      const wrap = b.el;
      const table = wrap.querySelector('table');
      const thead = table.querySelector('thead');
      const tbody = table.querySelector('tbody');
      const trs = [...tbody.children];
      const gap = px(getComputedStyle(wrap).marginTop);
      const theadH = thead ? thead.getBoundingClientRect().height : 0;
      const rowH = trs.map(tr => tr.getBoundingClientRect().height);
      const wrapAttrs = attrsOf(wrap);
      const theadHTML = thead ? thead.outerHTML : '';
      const CONT_H = 16;

      let idx = 0, firstChunk = true;
      while (idx < trs.length) {
        const lead = firstChunk ? gap : 6;
        const minNeed = lead + theadH + rowH[idx] + 2;
        if (cur > 0 && cur + minNeed > CH + EPS) newPage();
        cur += lead;
        let avail = CH - cur - theadH;
        const chunkRows = [];
        let usedRowsH = 0;
        while (idx < trs.length) {
          if (chunkRows.length > 0 && avail - rowH[idx] < -EPS) break;
          chunkRows.push(trs[idx].outerHTML);
          avail -= rowH[idx];
          usedRowsH += rowH[idx];
          idx++;
        }
        const more = idx < trs.length;
        place({ kind: 'table', wrapAttrs, theadHTML, rows: chunkRows, cont: more, contFrom: !firstChunk },
          theadH + usedRowsH + (more ? CONT_H : 0));
        firstChunk = false;
        if (more) newPage();
      }
    }
    return pages;
  }

  function tableChunkHTML(it) {
    const cont = it.cont ? '<div class="pbd-items-cont">Continued on next page →</div>' : '';
    const from = it.contFrom ? '<div class="pbd-items-cont" style="text-align:left;padding-bottom:4px">… continued</div>' : '';
    return `<div${it.wrapAttrs}>${from}<table class="pbd-items">${it.theadHTML}<tbody>${it.rows.join('')}</tbody></table>${cont}</div>`;
  }

  // ── running header / footer band markup ─────────────────────
  function resolveSlot(key, r) {
    switch (key) {
      case 'company': return r.company || '';
      case 'title': return r.title || '';
      case 'docref': return r.ref || '';
      case 'date': return r.date || '';
      case 'none': return '';
      default: return key || '';
    }
  }

  function HeaderBand({ cfg, r }) {
    const left = resolveSlot(cfg.left || 'company', r);
    const right = resolveSlot(cfg.right || 'docref', r);
    return (
      <div className="pbd-runhead" style={{ height: cfg.height || 34 }}>
        {cfg.showLogo && <img src="assets/ERPForce_Logo-Mark.png" alt="" />}
        {left && <span className="rh-l">{left}</span>}
        {right && <span className="rh-r">{right}</span>}
      </div>
    );
  }

  function FooterBand({ cfg, r, pageIndex, total }) {
    const fmt = (cfg.format || 'Page {n} of {total}')
      .replace('{n}', pageIndex + 1).replace('{page}', pageIndex + 1).replace('{total}', total);
    return (
      <div className="pbd-runfoot" style={{ height: cfg.height || 34 }}>
        <span className="rf-l">{r.footerText || ''}</span>
        {cfg.pageNumbers !== false && <span className="rf-r">{fmt}</span>}
      </div>
    );
  }

  // reserved padding constants for stationery modes
  const ST_PAD_T = 26, ST_PAD_B = 22, BLANK_PAD = 48, PRE_PAD = 8;

  // ── main ────────────────────────────────────────────────────
  function PaperDoc({ html, running, frame, density, accent, headWeight, logoSize, headStyle,
                      paper, orient, editable, onEdit, onPages, templateEditable, onTemplateEdit, onTokenClick,
                      stationery, stationeryKind }) {
    const measureRef = useRef(null);
    const pagesRef = useRef(null);
    const stHeadRef = useRef(null);
    const stHeadCRef = useRef(null);
    const stFootRef = useRef(null);
    const stFootCRef = useRef(null);
    const [pages, setPages] = useState([]);
    const D = dims(paper || 'A4', orient || 'portrait');
    const M = margins(frame);

    const isPreset = stationeryKind === 'preset' && !!stationery;
    const isPre = stationeryKind === 'preprinted' && !!stationery;
    const isBlank = stationeryKind === 'blank';
    const stnManaged = isPreset || isPre || isBlank;   // body masthead/footer suppressed
    const stSig = stationery ? JSON.stringify(stationery) : '';
    const frameSig = JSON.stringify(frame || {});

    const docStyle = {
      ['--pbd-accent']: accent || '#1B2C6E',
      ['--pbd-head-weight']: headWeight || 700,
      ['--pbd-logo-h']: (logoSize || 24) + 'px',
    };

    useLayoutEffect(() => {
      const root = measureRef.current;
      if (!root) return;
      root.style.width = D.cw + 'px';
      root.innerHTML = html;
      let cancelled = false;
      const run = () => {
        if (cancelled) return;
        // usable content height for a given page — header/footer differ per page
        const chFn = (pi) => {
          let top, bot;
          if (isPreset) {
            const fullH = (stationery.header.show && stHeadRef.current) ? stHeadRef.current.offsetHeight : 0;
            const compH = (stationery.header.show && stHeadCRef.current) ? stHeadCRef.current.offsetHeight : 0;
            const footH = (stationery.footer.show && stFootRef.current) ? stFootRef.current.offsetHeight : 0;
            const footCH = (stationery.footer.show && stFootCRef.current) ? stFootCRef.current.offsetHeight : 0;
            let hh = 0;
            if (stationery.header.show && (stationery.header.repeat === 'all' || pi === 0)) {
              hh = fullH;
            }
            let fh = 0;
            if (stationery.footer.show && (stationery.footer.repeat === 'all' || pi === 0)) {
              fh = footH;
            }
            top = hh + ST_PAD_T; bot = fh + ST_PAD_B;
          } else if (isPre) {
            top = (stationery.reserve.top || 0) + PRE_PAD; bot = (stationery.reserve.bottom || 0) + PRE_PAD;
          } else if (isBlank) {
            top = BLANK_PAD; bot = BLANK_PAD;
          } else {
            top = M.top; bot = M.bot;
          }
          return D.h - top - bot;
        };
        try { setPages(paginate(root, chFn)); } catch (e) { console.error('paginate', e); setPages([[{ kind: 'block', html }]]); }
      };
      run();
      const id = requestAnimationFrame(run);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      return () => { cancelled = true; cancelAnimationFrame(id); };
    }, [html, density, accent, headWeight, logoSize, headStyle, paper, orient, stationeryKind, stSig, frameSig]);

    useEffect(() => { if (onPages) onPages(pages.length); }, [pages]);

    // ── inline WYSIWYG editing wiring ──────────────────────────
    useEffect(() => {
      const root = pagesRef.current; if (!root) return;
      const eds = root.querySelectorAll('[data-edit]');
      if (!editable) { eds.forEach(el => el.removeAttribute('contenteditable')); return; }
      eds.forEach(el => { el.setAttribute('contenteditable', 'true'); el.classList.add('pbd-ed'); });

      const isMultiline = (el) => el.classList.contains('pbd-notes-body');
      const onFocusOut = (e) => {
        const el = e.target.closest && e.target.closest('[data-edit]');
        if (!el || !root.contains(el)) return;
        el.classList.remove('is-editing');
        if (onEdit) onEdit(el.getAttribute('data-edit'), el.getAttribute('data-edit-type') || 'text', el.innerText.trim());
      };
      const onFocusIn = (e) => { const el = e.target.closest && e.target.closest('[data-edit]'); if (el) el.classList.add('is-editing'); };
      const onKey = (e) => {
        const el = e.target.closest && e.target.closest('[data-edit]'); if (!el) return;
        if (e.key === 'Enter' && !e.shiftKey && !isMultiline(el)) { e.preventDefault(); el.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); el.blur(); }
      };
      const onPaste = (e) => {
        const el = e.target.closest && e.target.closest('[data-edit]'); if (!el) return;
        e.preventDefault();
        const text = (e.clipboardData || clipboardData).getData('text/plain');
        document.execCommand('insertText', false, text);
      };
      root.addEventListener('focusout', onFocusOut, true);
      root.addEventListener('focusin', onFocusIn, true);
      root.addEventListener('keydown', onKey, true);
      root.addEventListener('paste', onPaste, true);
      return () => {
        root.removeEventListener('focusout', onFocusOut, true);
        root.removeEventListener('focusin', onFocusIn, true);
        root.removeEventListener('keydown', onKey, true);
        root.removeEventListener('paste', onPaste, true);
      };
    }, [pages, editable, onEdit]);

    // ── template WYSIWYG editing (Edit-template lens) ──────────
    // Wording (.pbd-lit) is directly editable text; field chips
    // (.pbd-ph) open a field picker on click (no typing). Each commit
    // maps back to a source-template splice.
    useEffect(() => {
      const root = pagesRef.current; if (!root) return;
      const lits = root.querySelectorAll('.pbd-lit[data-k]');
      const chips = root.querySelectorAll('.pbd-ph[data-k]');
      if (!templateEditable) { lits.forEach(el => el.removeAttribute('contenteditable')); return; }
      lits.forEach(el => { el.setAttribute('contenteditable', 'true'); el.classList.add('pbd-ed'); });
      chips.forEach(el => { el.setAttribute('role', 'button'); el.setAttribute('tabindex', '0'); });

      const onClick = (e) => {
        const chip = e.target.closest && e.target.closest('.pbd-ph[data-k]');
        if (!chip || !root.contains(chip)) return;
        e.preventDefault();
        if (onTokenClick) onTokenClick(Number(chip.getAttribute('data-k')), chip.getBoundingClientRect());
      };
      const onFocusOut = (e) => {
        const el = e.target.closest && e.target.closest('.pbd-lit[data-k]');
        if (!el || !root.contains(el)) return;
        el.classList.remove('is-editing');
        if (onTemplateEdit) onTemplateEdit(Number(el.getAttribute('data-k')), el.textContent);
      };
      const onFocusIn = (e) => { const el = e.target.closest && e.target.closest('.pbd-lit[data-k]'); if (el) el.classList.add('is-editing'); };
      const onKey = (e) => {
        const el = e.target.closest && e.target.closest('.pbd-lit[data-k]'); if (!el) return;
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); el.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); el.blur(); }
      };
      const onPaste = (e) => {
        const el = e.target.closest && e.target.closest('.pbd-lit[data-k]'); if (!el) return;
        e.preventDefault();
        const text = (e.clipboardData || clipboardData).getData('text/plain');
        document.execCommand('insertText', false, text);
      };
      root.addEventListener('click', onClick, true);
      root.addEventListener('focusout', onFocusOut, true);
      root.addEventListener('focusin', onFocusIn, true);
      root.addEventListener('keydown', onKey, true);
      root.addEventListener('paste', onPaste, true);
      return () => {
        root.removeEventListener('click', onClick, true);
        root.removeEventListener('focusout', onFocusOut, true);
        root.removeEventListener('focusin', onFocusIn, true);
        root.removeEventListener('keydown', onKey, true);
        root.removeEventListener('paste', onPaste, true);
      };
    }, [pages, templateEditable, onTemplateEdit, onTokenClick]);

    const total = pages.length;
    const r = running || {};
    const f = frame || {};

    const innerPad = isPreset ? `${ST_PAD_T}px ${PAD_X}px ${ST_PAD_B}px`
      : isPre ? `${PRE_PAD}px ${PAD_X}px`
      : isBlank ? `${BLANK_PAD}px ${PAD_X}px`
      : `${M.top}px ${PAD_X}px ${M.bot}px`;
    const legacyBands = !isPreset && !isPre && !isBlank;

    return (
      <>
        {/* off-screen measuring surface (document body) */}
        <div ref={measureRef} className={`pbd ${stnManaged ? 'pb-stn' : ''}`} aria-hidden="true" data-density={density} data-head={headStyle || 'standard'}
          style={{ ...docStyle, position: 'fixed', left: -99999, top: 0, width: D.cw, visibility: 'hidden', pointerEvents: 'none' }} />

        {/* off-screen measuring surface (stationery header + footer heights) */}
        {isPreset && (
          <div className="pb-pages st" aria-hidden="true"
            style={{ position: 'fixed', left: -99999, top: 0, width: D.w, visibility: 'hidden', pointerEvents: 'none', ['--st-brand']: stationery.brand }}>
            <div className="pbd-page" style={{ width: D.w, minHeight: 0, boxShadow: 'none' }}>
              {stationery.header.show && <div ref={stHeadRef}><STDoc.Header m={stationery} /></div>}
              {stationery.header.show && <div ref={stHeadCRef}><STDoc.Header m={stationery} compact /></div>}
              {stationery.footer.show && <div ref={stFootRef}><STDoc.Footer m={stationery} pageIndex={0} total={2} /></div>}
              {stationery.footer.show && <div ref={stFootCRef}><STDoc.Footer m={stationery} pageIndex={1} total={2} compact /></div>}
            </div>
          </div>
        )}

        <div ref={pagesRef}
          className={`pb-pages ${editable ? 'pb-editable' : ''} ${templateEditable ? 'pb-tpl-editable' : ''} ${isPreset ? 'st' : ''} ${stnManaged ? 'pb-stn' : ''}`}
          style={isPreset ? { ['--st-brand']: stationery.brand } : undefined}>
          {pages.map((items, pi) => {
            const stHeadShow = isPreset && stationery.header.show && (stationery.header.repeat === 'all' || pi === 0);
            const stHeadCompact = false;
            const stFootShow = isPreset && stationery.footer.show && (stationery.footer.repeat === 'all' || pi === 0);
            const stFootCompact = false;
            return (
              <div className="pbd-page" key={pi} style={{ width: D.w, minHeight: D.h }} data-screen-label={`Page ${pi + 1}`}>
                {isPreset && stationery.watermark.show && <STDoc.Watermark m={stationery} />}
                {isPre && <div className="st-reserve st-reserve--top" style={{ height: stationery.reserve.top }}>
                  <span><Ic k="panelL" />Reserved · pre-printed letterhead</span>
                </div>}
                {stHeadShow && <STDoc.Header m={stationery} compact={stHeadCompact} />}
                {legacyBands && bandShown(f.header, pi) && <HeaderBand cfg={f.header} r={r} />}

                <div className="pbd pbd-page-inner" data-density={density} data-head={headStyle || 'standard'}
                  style={{ ...docStyle, padding: innerPad }}>
                  {items.map((it, ii) => (
                    <div key={ii} style={{ display: 'contents' }}
                      dangerouslySetInnerHTML={{ __html: it.kind === 'block' ? it.html : tableChunkHTML(it) }} />
                  ))}
                </div>

                {stFootShow && <STDoc.Footer m={stationery} pageIndex={pi} total={total} compact={stFootCompact} />}
                {isPre && <div className="st-reserve st-reserve--bot" style={{ height: stationery.reserve.bottom }}>
                  <span><Ic k="panelR" />Reserved · pre-printed footer</span>
                </div>}
                {legacyBands && bandShown(f.footer, pi) && <FooterBand cfg={f.footer} r={r} pageIndex={pi} total={total} />}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  export const PBPaper = { PaperDoc, dims };

